/**
 * Which fix to publish out of a batch the OS delivered in one callback.
 * Imports nothing so a plain node test can drive it (the background task
 * pulls expo-location and cannot be).
 *
 * Replaces the `locations[0]` bug: Core Location delivers entries "in the
 * order in which they occurred", so the newest fix is the LAST element.
 * Unnoticed because single-fix batches make [0] and newest identical; it
 * diverges exactly when iOS defers delivery — a 40-minute locked soak on an
 * iPhone SE measured deferral gaps of 174.3 s and 215.3 s, i.e. when the
 * position matters most.
 *
 * Greatest-timestamp rather than `at(-1)`: same answer for an ordered batch,
 * right answer for an unordered one — correctness stops depending on a
 * platform ordering contract we do not control (the test drives reversed and
 * shuffled batches for this reason).
 *
 * Undated fixes NEVER win: fix-age.ts treats an undated CHOSEN fix as age 0,
 * but borrowing that rule to CHOOSE would let an undated entry outrank every
 * dated one — and publishing it stamps a coordinate of unknown age as
 * captured now, the precise lie the age parameter stops. Dated entries rank
 * among themselves and beat undated ones; a batch with no usable timestamp
 * yields null (same trade stay-visible.ts makes for a fix with no accuracy:
 * lapsing is recoverable, a falsely-current coordinate is not).
 */

/** The slice of `Location.LocationObject` this needs. */
export type TimestampedFix = {
  /** Milliseconds since the epoch, per `LocationObject.timestamp`. */
  timestamp?: number | null;
};

/**
 * The freshest fix in a delivered batch, or `null` when there is nothing to
 * publish.
 *
 * Never throws: it runs on the one path that must not fail, and an empty or
 * malformed batch is answered with `null` so the caller records `no_fix` and
 * exits cleanly rather than the task dying before its own diagnostics.
 */
export function newestFix<T extends TimestampedFix>(
  locations: readonly T[] | null | undefined,
): T | null {
  if (!locations || locations.length === 0) return null;

  let best: T | null = null;
  let bestAt = -Infinity;

  for (const fix of locations) {
    if (!fix) continue;
    const at = fix.timestamp;
    // `>=`, so on equal timestamps the LATER entry wins. Both are the same age
    // and neither is more correct; taking the later one simply matches arrival
    // order. Stated plainly because the previous comment claimed this made the
    // result independent of iteration order, which was false — first-wins is
    // just as order-dependent, only deterministic.
    if (typeof at === "number" && Number.isFinite(at) && at >= bestAt) {
      best = fix;
      bestAt = at;
    }
  }

  // `null` when nothing carries a usable timestamp — see the header. The caller
  // records `no_fix` and publishes nothing, rather than sending a coordinate of
  // unknown age that the server would date as captured now.
  return best;
}

/**
 * Ages of a batch, for the probe — how stale the oldest and newest entries were
 * and which one was taken.
 *
 * Reported so the fix above can be shown to do something on a real device.
 * Without it a build carrying this change is indistinguishable from one without
 * it: batches of one behave identically either way, and nothing in the log says
 * whether a multi-fix batch ever arrived. `selectedAgeMs` should equal
 * `newestAgeMs` on every delivery — a run where it equals `oldestAgeMs` instead
 * means this module is not being consulted.
 */
export function batchAges(
  locations: readonly TimestampedFix[] | null | undefined,
  now: number,
): { count: number; oldestAgeMs: number; newestAgeMs: number } | null {
  if (!locations || locations.length === 0) return null;

  // Constant space, and scalar min/max rather than `Math.max(...ages)`. The OS
  // decides how many fixes a deferred delivery carries, so the array length is
  // not ours to bound — and spreading a large array into a call throws
  // RangeError. This runs BEFORE the task's own try/catch, so that throw would
  // escape the callback: a diagnostic that kills the wake it is measuring.
  // `timing-probe.ts` makes "measurement must not alter or break" its second
  // property; building the array was a way of breaking it in the one function
  // whose entire job is to observe.
  let oldest = 0;
  let newest = 0;
  let seen = false;

  for (const fix of locations) {
    const at = fix?.timestamp;
    if (typeof at !== "number" || !Number.isFinite(at)) continue;
    const age = Math.max(0, now - at);
    if (!seen) {
      oldest = age;
      newest = age;
      seen = true;
      continue;
    }
    if (age > oldest) oldest = age;
    if (age < newest) newest = age;
  }

  // `seen === false` — nothing in the batch carried a usable timestamp —
  // reports 0/0 and the true count, rather than the `Infinity`/`-Infinity` that
  // seeding the scalars with sentinels would leave behind.
  //
  // Defensive, NOT observable. An earlier comment here claimed the count was
  // "the one thing that tells us batching happens on a real device at all",
  // which is false end to end: `newestFix` returns null for such a batch and
  // `stay-visible.ts:227` exits at `no_fix` before reaching the `batchAges`
  // call at :238, so this branch never reaches a probe. It is unreachable a
  // second way too — `LocationObject.timestamp` is a required `number`, and
  // both native sides always emit one (`LocationResults.kt:111` from
  // `location.time`; `LocationUtils.swift:33` unconditionally).
  return { count: locations.length, oldestAgeMs: oldest, newestAgeMs: newest };
}
